/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { breakpoints } from '@coursera/cds-core';

import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';

type Props = {
  minimizedChat: React.ReactNode;
};

const styles = {
  chatStyles: css`
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 3001;
    box-shadow: 0 0 28px -6px rgb(0 0 0 / 16%), 0 0 28px -12px rgb(0 0 0 / 16%);
    transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
    transform-origin: bottom left;

    ${breakpoints.down('sm')} {
      box-shadow: none;
    }
  `,
  fullScreenChatSize: css`
    width: 100%;
    height: 100%;
  `,
  minimizedChatSize: css`
    transform: scale(0);
    opacity: 0;
  `,
  childContainer: css`
    position: relative;
    height: 100%;

    ${breakpoints.down('sm')} {
      max-width: 100%;
      margin: 0;
      height: 100%;
    }
  `,
  hidden: css`
    display: none;
  `,
};

const FullScreenCoachChatContainer = ({ children, minimizedChat }: React.PropsWithChildren<Props>) => {
  const { isMinimizedChat } = useCoachState();
  const [isHidden, setIsHidden] = useState(false);
  useEffect(() => {
    // disable page scrolling when the chat is fullscreen
    document.body.style.overflow = isMinimizedChat ? 'visible' : 'hidden';

    // remove the hidden style before the chat transition back to fullscreen
    if (!isMinimizedChat) {
      setIsHidden(false);
    }

    return () => {
      // always restore to visible on unmount
      document.body.style.overflow = 'visible';
    };
  }, [isMinimizedChat]);

  const onTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    // hide from screen readers and also prevent keyboard focus when the chat is minimized
    if (isMinimizedChat) {
      setIsHidden(true);
    }
  };

  return (
    <React.Fragment>
      {isMinimizedChat && minimizedChat}
      {/* Keep the chat container mounted to avoid fetching chat history again when the chat is reopened. */}
      <div
        css={[styles.chatStyles, styles.fullScreenChatSize, isMinimizedChat && styles.minimizedChatSize]}
        aria-hidden={isMinimizedChat}
        onTransitionEnd={onTransitionEnd}
      >
        <div css={[styles.childContainer, isHidden && styles.hidden]}>{children}</div>
      </div>
    </React.Fragment>
  );
};

export default FullScreenCoachChatContainer;
